import React, { useMemo, useState } from "react";
import {
  MapPin, Wallet, BedDouble, Bath, Car, Building2, Compass, Search,
  ChevronRight, ChevronDown, Plus, Check, Users, Loader2, Target,
} from "lucide-react";
import ContactLink from "../../contacts/components/ContactLink";

/**
 * The Buyers Match search controls in the campaign editor.
 *
 * Christian (Monday 12567166271): "Needs to be setup similar to this. We need to
 * be able to search buyers and add them into our search", pointing at the
 * "Find Buyers for a Property" panel. So the campaign audience is no longer
 * whatever the listing happens to imply: the agent sets the requirements
 * (suburbs, budget, beds, baths, cars, type, radius) exactly like that panel,
 * AND can search any buyer by name and add them to the send by hand.
 *
 * Purely presentational. All state lives in CampaignEditorPage.
 */

const PROPERTY_TYPES = [
  "House", "Apartment", "Unit", "Townhouse", "Villa",
  "House and Land", "Land", "Block of Units",
];
const RADIUS_OPTIONS = [2, 5, 10, 15, 25];
const AU_STATES = ["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

const SUBURB_KIND = {
  exact: { label: "This suburb", chip: "bg-emerald-100 text-emerald-700" },
  surrounding: { label: "Surrounding", chip: "bg-sky-100 text-sky-700" },
  other: { label: "Other areas", chip: "bg-gray-100 text-gray-600" },
};

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 mb-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]";

/** A min "to" max pair of number inputs. `name` distinguishes the four pairs for
 *  screen readers, since the placeholders alone are all "Min"/"Max". */
const RangeInputs = ({ name, minValue, maxValue, onMin, onMax }) => (
  <div className="flex items-center gap-1.5">
    <input
      type="number"
      value={minValue}
      onChange={(e) => onMin(e.target.value)}
      placeholder="Min"
      aria-label={`${name} minimum`}
      className={inputCls}
    />
    <span className="text-[11px] text-gray-400">to</span>
    <input
      type="number"
      value={maxValue}
      onChange={(e) => onMax(e.target.value)}
      placeholder="Max"
      aria-label={`${name} maximum`}
      className={inputCls}
    />
  </div>
);

export default function BuyerMatchSearchPanel({
  propertyLabel,
  criteria,
  onCriteria,
  suburbs = [],
  suburbsLoading = false,
  selectedSuburbs,
  onToggleSuburb,
  onSetSuburbs,
  onRun,
  running = false,
  onReset,
  isCustom = false,
  channel = "email",
  // Add-a-buyer-by-name search
  searchQuery,
  onSearchQuery,
  searchResults = [],
  searching = false,
  onAddBuyer,
  addedIds,
}) {
  const [suburbsOpen, setSuburbsOpen] = useState(false);
  const [suburbFilter, setSuburbFilter] = useState("");

  const grouped = useMemo(() => {
    const q = suburbFilter.trim().toLowerCase();
    const rows = q ? suburbs.filter((s) => s.suburb.toLowerCase().includes(q)) : suburbs;
    return {
      exact: rows.filter((s) => s.kind === "exact"),
      surrounding: rows.filter((s) => s.kind === "surrounding"),
      other: rows.filter((s) => s.kind === "other"),
    };
  }, [suburbs, suburbFilter]);

  const set = (key) => (value) => onCriteria(key, value);

  const tickedSummary = selectedSuburbs.size
    ? Array.from(selectedSuburbs).slice(0, 4).join(", ") +
      (selectedSuburbs.size > 4 ? ` +${selectedSuburbs.size - 4} more` : "")
    : "none ticked yet";

  return (
    <div className="space-y-4">
      {/* 1 · Requirements ---------------------------------------------------- */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">1 · Requirements</p>
          {isCustom && (
            <button onClick={onReset} className="text-[11px] font-bold text-gray-500 hover:underline">
              {/* With no listing there is nothing to reset TO, so it just clears. */}
              {propertyLabel ? "Reset to the listing" : "Clear the requirements"}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field icon={MapPin} label="Suburb">
            <input
              value={criteria.suburb}
              onChange={(e) => set("suburb")(e.target.value)}
              placeholder="e.g. Fawkner"
              className={inputCls}
            />
          </Field>
          <Field label="State">
            <select value={criteria.state} onChange={(e) => set("state")(e.target.value)} className={inputCls}>
              <option value="">Any state</option>
              {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field icon={Wallet} label="Price ($)">
            <RangeInputs
              name="Price"
              minValue={criteria.price_min}
              maxValue={criteria.price_max}
              onMin={set("price_min")}
              onMax={set("price_max")}
            />
          </Field>
          <Field icon={BedDouble} label="Bedrooms">
            <RangeInputs
              name="Bedrooms"
              minValue={criteria.beds_min}
              maxValue={criteria.beds_max}
              onMin={set("beds_min")}
              onMax={set("beds_max")}
            />
          </Field>
          <Field icon={Bath} label="Bathrooms">
            <RangeInputs
              name="Bathrooms"
              minValue={criteria.baths_min}
              maxValue={criteria.baths_max}
              onMin={set("baths_min")}
              onMax={set("baths_max")}
            />
          </Field>
          <Field icon={Car} label="Car spaces">
            <RangeInputs
              name="Car spaces"
              minValue={criteria.cars_min}
              maxValue={criteria.cars_max}
              onMin={set("cars_min")}
              onMax={set("cars_max")}
            />
          </Field>
          <Field icon={Building2} label="Type">
            <select
              value={criteria.property_type}
              onChange={(e) => set("property_type")(e.target.value)}
              className={inputCls}
            >
              <option value="">Any</option>
              {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field icon={Compass} label="Radius">
            <select
              value={criteria.radius_km}
              onChange={(e) => set("radius_km")(Number(e.target.value))}
              className={inputCls}
            >
              {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} km</option>)}
            </select>
          </Field>
        </div>
        <p className="mt-2 text-[11px] text-amber-700">
          Leave a field blank to ignore it. Budget matching uses a 20% range: buyers whose stated budget is
          more than 20% away from the price are left out. (Buyers who never set a budget still show.)
        </p>
      </div>

      {/* 2 · Suburbs buyers have marked -------------------------------------- */}
      <div className="border-t border-gray-100 pt-3">
        <button
          onClick={() => setSuburbsOpen((o) => !o)}
          className="w-full flex items-center justify-between text-left"
        >
          <span className="flex items-center gap-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wide">
            {suburbsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            2 · Suburbs buyers have marked
          </span>
          <span className="text-[11px] text-gray-500 normal-case font-normal truncate max-w-[55%]">
            {suburbsLoading ? "loading…" : tickedSummary}
          </span>
        </button>

        {suburbsOpen && (
          <div className="mt-2">
            <div className="flex items-center gap-2 mb-2">
              <input
                value={suburbFilter}
                onChange={(e) => setSuburbFilter(e.target.value)}
                placeholder="Filter suburbs"
                className={`${inputCls} max-w-[220px]`}
              />
              <button
                onClick={() => onSetSuburbs(suburbs.map((s) => s.suburb))}
                className="text-[11px] font-bold text-[#25AFC0] hover:underline"
              >
                Tick all
              </button>
              <button
                onClick={() => onSetSuburbs([])}
                className="text-[11px] font-bold text-gray-500 hover:underline"
              >
                Clear
              </button>
              <button
                onClick={() => onSetSuburbs(suburbs.filter((s) => s.recommended).map((s) => s.suburb))}
                className="text-[11px] font-bold text-gray-500 hover:underline"
              >
                Recommended only
              </button>
            </div>

            {suburbsLoading && (
              <p className="text-[11px] text-gray-400 py-2 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading the suburbs buyers have marked…
              </p>
            )}
            {!suburbsLoading && suburbs.length === 0 && (
              <p className="text-[11px] text-gray-400 py-2">
                No buyer has marked a suburb yet, so there's nothing to tick.
              </p>
            )}

            <div className="max-h-64 overflow-y-auto space-y-3">
              {["exact", "surrounding", "other"].map((kind) =>
                grouped[kind].length ? (
                  <div key={kind}>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold mb-1.5 ${SUBURB_KIND[kind].chip}`}>
                      {SUBURB_KIND[kind].label} ({grouped[kind].length})
                    </span>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                      {grouped[kind].map((s) => (
                        <label key={s.suburb} className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedSuburbs.has(s.suburb)}
                            onChange={() => onToggleSuburb(s.suburb)}
                          />
                          <span className="truncate text-gray-700">{s.suburb}</span>
                          <span className="text-[10px] text-gray-400 shrink-0">
                            {/* The state shows when it isn't the one being searched, so an
                                interstate area can't be mistaken for a nearby suburb. */}
                            {s.interstate && s.state ? `${s.state} · ` : ""}
                            {s.buyer_count}
                            {s.distance_km != null ? ` · ${s.distance_km}km` : ""}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}
      </div>

      {/* 3 · Search buyers and add them --------------------------------------- */}
      <div className="border-t border-gray-100 pt-3">
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">
          3 · Search buyers and add them
        </p>
        <p className="text-[11px] text-gray-400 mb-2">
          Someone you want in this send who doesn't match the requirements, like the buyer who rang about
          {propertyLabel ? ` ${propertyLabel}` : " a property"} but never set their preferences. Search
          them by name, email or phone and add them.
        </p>
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
          <input
            value={searchQuery}
            onChange={(e) => onSearchQuery(e.target.value)}
            placeholder="Search every buyer by name, email or phone"
            className="w-full border border-gray-300 rounded-lg pl-8 pr-2 py-2 text-sm"
          />
        </div>

        {searching && (
          <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Searching…
          </p>
        )}
        {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
          <p className="text-[11px] text-gray-400 mt-2">No buyer found for "{searchQuery.trim()}".</p>
        )}

        {searchResults.length > 0 && (
          <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
            {searchResults.map((r) => {
              const added = addedIds.has(r.contact_id);
              return (
                <div key={r.contact_id} className="flex items-center gap-2 px-3 py-2 text-xs">
                  <span className="flex-1 min-w-0">
                    <ContactLink
                      identity={{ contactId: r.contact_id, email: r.email, name: r.name, phone: r.phone }}
                      className="font-semibold text-[#3a99a3] hover:text-[#55C5D0] hover:underline block truncate text-left"
                    >
                      {r.name || "Unnamed buyer"}
                    </ContactLink>
                    <span className="block text-gray-400 truncate">
                      {r.email || r.phone || "No contact details"}
                      {r.contact_type ? ` · ${r.contact_type}` : ""}
                      {r.matched_suburb ? ` · wants ${r.matched_suburb}` : ""}
                    </span>
                    {!r.sendable && <span className="block text-amber-600">{r.blocked_reason}</span>}
                  </span>
                  <button
                    onClick={() => onAddBuyer(r)}
                    disabled={added || !r.sendable}
                    title={
                      !r.sendable
                        ? `Can't be reached by ${channel === "sms" ? "SMS" : "email"}: ${r.blocked_reason}`
                        : added
                          ? "Already in this send"
                          : "Add to this send"
                    }
                    className={`px-2 py-1 rounded-md text-[11px] font-bold inline-flex items-center gap-1 shrink-0 ${
                      added
                        ? "bg-green-100 text-green-700"
                        : r.sendable
                          ? "bg-[#55C5D0] text-white"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {added ? <><Check className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Add</>}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Run ------------------------------------------------------------------ */}
      <div className="border-t border-gray-100 pt-3 flex items-center gap-3">
        <button
          onClick={onRun}
          disabled={running || selectedSuburbs.size === 0}
          className="px-4 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
          Find buyers
        </button>
        <p className="text-[11px] text-gray-500 flex items-center gap-1">
          <Users className="w-3 h-3" />
          {selectedSuburbs.size === 0
            ? "Tick at least one suburb to search."
            : "Only buyers who meet every requirement you set are shown. Buyers you added by hand always stay."}
        </p>
      </div>
    </div>
  );
}
